import { MonitoringCategory } from "@domain/product/monitoring/monitoring-product.service";
import { ProductConfiguration } from "@domain/project/configurations/product-configuration";
import { ProjectEventType } from "@domain/project/project-event";
import type { ScopedProject } from "@domain/project/project-guards";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { Expose, Type } from "class-transformer";
import { IsEnum, IsOptional, IsString, ValidateNested } from "class-validator";
import { v4 as uuidv4 } from "uuid";

export class DisplayConfiguration extends ProductConfiguration {
  @IsString()
  public familyNumber: string;

  @IsEnum(MonitoringCategory)
  @Expose({ name: "category" })
  private _category!: MonitoringCategory;

  @Type(() => MonitoringProductItem)
  @Expose({ name: "display" })
  @ValidateNested()
  private _display!: MonitoringProductItem;

  @IsOptional()
  @Type(() => MonitoringProductItem)
  @ValidateNested({ each: true })
  @Expose({ name: "accessories" })
  private _accessories?: MonitoringProductItem[];

  constructor(
    id: string,
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    display: MonitoringProductItem,
    accessories?: MonitoringProductItem[],
  ) {
    super(id, name, project);
    this.familyNumber = familyNumber;
    this.category = category;
    this.display = display;
    this.accessories = accessories;
  }

  static create(
    name: string,
    project: ScopedProject,
    familyNumber: string,
    category: MonitoringCategory,
    display: MonitoringProductItem,
    accessories?: MonitoringProductItem[],
  ): DisplayConfiguration {
    return new DisplayConfiguration(uuidv4(), name, project, familyNumber, category, display, accessories);
  }

  update(familyName: string, familyNumber: string, display: MonitoringProductItem, accessories: MonitoringProductItem[]) {
    this.name = familyName;
    this.familyNumber = familyNumber;
    this.display = display;
    this.accessories = accessories;
    this.project.publishUpdate(ProjectEventType.DISPLAY_CONFIGURATION_UPDATED, this);
  }

  getDeviceMatNr(): string[] {
    return [...this.display.option.devices, ...this.accessoriesDevices];
  }

  override get mainProductId(): string {
    return this.familyNumber;
  }

  get category(): MonitoringCategory {
    return this._category;
  }

  get display(): MonitoringProductItem {
    return this._display;
  }

  get accessories(): MonitoringProductItem[] {
    return this._accessories ?? [];
  }

  set category(value: MonitoringCategory) {
    this._category = value;
  }

  set display(value: MonitoringProductItem) {
    this._display = value;
  }

  set accessories(value: MonitoringProductItem[] | undefined) {
    this._accessories = value;
  }

  get configuredProductIds(): string[] {
    return [...this.display.option.devices, ...this.accessoriesDevices];
  }

  getOptions(): MonitoringProductItem[] {
    return [this.display, ...(this._accessories ?? [])];
  }

  getAllItems(): MonitoringProductItem[] {
    return this.getOptions();
  }

  get accessoriesDevices(): string[] {
    return (this._accessories ?? []).flatMap((acc) => acc.option.devices);
  }

  countPlacedProducts(project: ScopedProject = this.project): number {
    return project ? project.countPlacedDisplays(this) : 0;
  }
}
